/**
 * Core-100 R5 CORE-BJ / OD-49 — shop-authorised support access (migration 20270206025600).
 *
 * The database is the boundary: every function below re-checks the platform switch (default OFF), the operator's
 * designation, the shop's grant (approved, unrevoked, unexpired) and the session on EVERY call. These wrappers only
 * call it and turn its refusal codes into sentences a person can act on. Nothing here decides access.
 *
 * The RPCs are not in the generated `types.ts` (generated from the hosted database), so the calls use the repository's
 * `supabase.rpc(name as never, args as never)` convention with locally declared row shapes. The client method is
 * always called on `supabase` itself (never detached).
 */
import { supabase } from '@/integrations/supabase/client';

export type ShopSupportScope = 'OPERATIONAL_READ' | 'OPERATIONAL_READ_WITH_CUSTOMER_NAMES';

/** One row of the shop's own view (`get_shop_support_access`). */
export interface ShopSupportAccessRow {
  grant_id: string;
  /** REQUESTED · APPROVED (not yet opened) · ACTIVE (opened, within its bound) · EXPIRED · DENIED · REVOKED · STALE */
  status: string;
  request_purpose: string;
  requested_scope: ShopSupportScope;
  granted_scope: ShopSupportScope | null;
  authorisation_basis: string | null;
  operator_label: string;
  requested_at: string;
  decided_at: string | null;
  access_expires_at: string | null;
  session_started_at: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  read_count: number;
  last_read_at: string | null;
  can_decide: boolean;
}

/** One row of the operator's own view (`support_list_my_shop_access`). */
export interface MyShopSupportAccessRow {
  grant_id: string;
  shop_id: string;
  /** Only once the shop approved; requesting an id discloses nothing about the shop. */
  shop_name: string | null;
  /** REQUESTED · APPROVED · USED · EXPIRED · DENIED · REVOKED */
  status: string;
  requested_scope: ShopSupportScope;
  granted_scope: ShopSupportScope | null;
  request_purpose: string;
  requested_at: string;
  access_expires_at: string | null;
  session_id: string | null;
  revoke_reason: string | null;
}

export interface GrantedSessionStart {
  session_id: string;
  grant_id: string;
  shop_id: string;
  shop_name: string | null;
  scope: ShopSupportScope;
  expires_at: string;
}

const MESSAGES: Array<[string, string]> = [
  ['SUPPORT_SHOP_ACCESS_DISABLED', 'Shop-authorised support access is turned off for this shop. Nothing can be opened or read.'],
  ['SUPPORT_OPERATOR_REQUIRED', 'Only a designated OptiBay support operator can do this. If your designation was removed, access ended with it.'],
  ['SUPPORT_GRANT_OPERATOR_NOT_DESIGNATED', 'Your support-operator designation changed after this request, so the grant no longer applies. Ask the shop for a new one.'],
  ['SUPPORT_GRANT_NOT_APPROVED', 'The shop has not approved this access. Ask an owner or manager of the shop to approve the request.'],
  ['SUPPORT_GRANT_REVOKED', 'The shop revoked this access. Nothing more can be read; a new approval is needed.'],
  ['SUPPORT_GRANT_EXPIRED', "The shop's approval has expired. Access is not extended; ask the shop for a new approval."],
  ['SUPPORT_GRANT_ALREADY_USED', 'This approval already opened its session. Each approval opens one session; ask the shop for a new one.'],
  ['SUPPORT_GRANT_NOT_YOURS', 'This access belongs to someone else, or the session is not backed by a shop approval.'],
  ['SUPPORT_GRANT_SELF_APPROVAL', 'The person who asked for access cannot approve it. Another owner or manager of the shop must decide.'],
  ['SUPPORT_GRANT_SHOP_MANAGER_REQUIRED', 'Only an owner or manager of this shop can decide or revoke support access.'],
  ['SUPPORT_GRANT_ALREADY_DECIDED', 'This request has already been decided.'],
  ['SUPPORT_GRANT_REQUEST_STALE', 'This request is more than 24 hours old and can no longer be approved. Support must ask again.'],
  ['SUPPORT_GRANT_BASIS_REQUIRED', 'State the basis on which the shop authorises this access (for example, who asked for help).'],
  ['SUPPORT_GRANT_PURPOSE_REQUIRED', 'State why access is needed (at least 10 characters). The shop sees it.'],
  ['SUPPORT_GRANT_SCOPE_INVALID', 'A shop can narrow the requested scope, never widen it.'],
  ['SUPPORT_GRANT_LIFETIME_INVALID', 'Support access lasts between 5 and 60 minutes.'],
  ['SUPPORT_GRANT_BOUND', "A shop-authorised session cannot be extended past the shop's approval. Ask the shop for a new one."],
  ['SHOP_MEMBER_REQUIRED', 'Only members of this shop can see its support access.'],
  ['SUPPORT_OPT_IN_SHOP_MANAGER_REQUIRED', 'Only an owner or manager of this shop can decide whether OptiBay support may ask for access.'],
];

/** The person-readable refusal for a server error, or null when the error is not one of this mechanism's refusals. */
export function describeShopSupportAccessError(error: { message?: string | null } | null | undefined): string | null {
  const msg = error?.message ?? '';
  for (const [code, text] of MESSAGES) {
    if (msg.includes(code)) return text;
  }
  if (/already holds an effective support session/i.test(msg)) {
    return 'You already have an open support session. End it before opening another shop.';
  }
  return null;
}

async function call<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.rpc(fn as never, args as never);
  if (error) throw error;
  return data as T;
}

export const requestShopSupportAccess = (shopId: string, purpose: string, scope: ShopSupportScope, bugId?: string | null) =>
  call<{ grant_id: string; status: string; decision: string }>('support_request_shop_access', {
    p_shop_id: shopId, p_purpose: purpose, p_scope: scope, p_bug_id: bugId ?? null,
  });

export const listMyShopSupportAccess = () => call<MyShopSupportAccessRow[]>('support_list_my_shop_access');

export const startGrantedSupportSessionRpc = (grantId: string, disclosureReason: string, note?: string) =>
  call<GrantedSessionStart>('support_start_granted_session', {
    p_grant_id: grantId, p_disclosure_reason: disclosureReason, p_note: note ?? null,
  });

export const readGrantedShop = (sessionId: string, resource: 'OVERVIEW' | 'TICKETS') =>
  call<Record<string, unknown>>('support_read_granted_shop', { p_session_id: sessionId, p_resource: resource });

export const getShopSupportAccess = (shopId: string) => call<ShopSupportAccessRow[]>('get_shop_support_access', { p_shop_id: shopId });

export const decideShopSupportAccess = (grantId: string, decision: 'APPROVE' | 'DENY', basis?: string, scope?: ShopSupportScope, minutes?: number) =>
  call<{ grant_id: string; status: string }>('shop_support_access_decide', {
    p_grant_id: grantId, p_decision: decision, p_authorisation_basis: basis ?? null, p_scope: scope ?? null, p_minutes: minutes ?? null,
  });

export const revokeShopSupportAccess = (grantId: string, reason?: string) =>
  call<{ grant_id: string; status: string }>('shop_support_access_revoke', { p_grant_id: grantId, p_reason: reason ?? null });

/** OD-49: the shop's standing opt-in — whether OptiBay support may ASK for access at all (each request still needs an
 *  owner/manager approval). No row = not opted in. Readable by every member of the shop. */
export interface ShopSupportOptIn { opted_in: boolean; decided_at: string; reason: string | null }

export async function getShopSupportOptIn(shopId: string): Promise<ShopSupportOptIn | null> {
  const { data, error } = await supabase.from('shop_support_access_opt_ins' as never)
    .select('opted_in, decided_at, reason').eq('shop_id', shopId).maybeSingle();
  if (error) throw error;
  return (data as ShopSupportOptIn | null) ?? null;
}

export const setShopSupportOptIn = (shopId: string, optIn: boolean, reason?: string) =>
  call<{ shop_id: string; opted_in: boolean }>('set_shop_support_access_opt_in', { p_shop_id: shopId, p_opt_in: optIn, p_reason: reason ?? null });

export const SCOPE_LABEL: Record<ShopSupportScope, string> = {
  OPERATIONAL_READ: 'Read-only operational view (customers redacted)',
  OPERATIONAL_READ_WITH_CUSTOMER_NAMES: 'Read-only operational view including customer names',
};
